import { type HTMLAttributes, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { DndContext } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useQuery } from '@tanstack/react-query';
import { Layout as LayoutIcon, Plus, X } from 'lucide-react';

import { Dropdown } from '@/components/Dropdown';
import { type LayoutElement } from '@/components/form/RecursiveFormRenderer';
import { usePaletteDnD } from '@/hooks/usePaletteDnD';
import { useSystemModuleActiveLayout } from '@/hooks/useSystemModuleActiveLayout';
import { api } from '@/lib/api';
import { normalizeSearch } from '@/lib/search-normalize';

import { DragHandle } from './DragHandle';
import { FieldBlock } from './FieldBlock';
import { LayoutElementsPalette } from './LayoutElementsPalette';
import { PaletteSearchInput } from './PaletteSearchInput';
import { SubcollectionFieldButton } from './SubcollectionFieldButton';

/**
 * SubcollectionLayoutEditor v4 — F12 PR 2 pkt 2 iter 2a (2026-05-19).
 *
 * Pełen builder canvas dla subcollection w zakładce parent modułu (np. Kontakty
 * w builderze Kontrahentów). Niezależna instancja layoutu sekcji + pól helper-encji,
 * NIEZALEŻNA od głównego buildera modułu wskazywanego. Default seed = klon
 * aktywnego layoutu helper-encji przy pierwszym wjeździe.
 *
 * v4 nad v3 (iter 2a):
 *  - Własny `DndContext` z `usePaletteDnD` hook (3 callbacks: addFromPalette /
 *    sectionReorder / elementReorder; akordeon + stepper no-op).
 *  - Aside paleta po lewej z tabs switcher **Pola | Klocki**:
 *    - „Pola" → `SubcollectionFieldButton` dla każdego pola helper-encji nieużytego
 *      w żadnej sekcji (filter `usedFieldIds`). Drag source `kind='field'`.
 *    - „Klocki" → `LayoutElementsPalette` (drag source `kind='layout-element'`).
 *  - Sortable sekcje (`SortableContext` + `useSortable`) — drag handle na headerze.
 *  - Sortable pola w sekcji (`SortableContext` + `FieldBlock.sortableContainerId`)
 *    — drag handle 6-kropek per pole. Cross-section move supported (per
 *    `usePaletteDnD.onElementReorder` z `sourceContainerId !== targetContainerId`).
 *  - Side panel `FieldOverrideMiniPanel` — DEPRIORITIZED (per ADR-0030 + recon
 *    2026-05-19 6 subagentów: override jest właściwością kategorii, edycja w
 *    głównym builderze helper-encji = single source of truth, side panel by
 *    duplikował UI z tym samym DB row).
 *
 * Storage (parent module layout_config JSONB):
 *  - sekcje + pola w `tab.subcollectionFormLayout` (LayoutSection[])
 *  - adapter (`layout-shape-adapter.ts`) save'uje to jako element typu
 *    `subcollection_form_layout` z children=sections w `TabItem.children`
 *    (po `related_records_table`)
 *  - backward compat: v2 element `related_records_table` z displayFields
 *    + allowAdd/Edit/Delete w `tab.extras` przez `element` prop / `onChange`
 *
 * Wymóg: rodzic (`SystemModuleBuilderPage`) renderuje v4 full-width gdy
 * `selectedTab.type === 'subcollection'` (zamiast standardowego aside+middle+aside
 * canvas-non-fields). v4 ma własną paletę po lewej, więc globalna aside z
 * `LayoutElementsPalette` jest tutaj NIEPOTRZEBNA.
 */

export interface SubcollectionLayoutElement {
  id: string;
  type: 'related_records_table';
  settings: {
    collection?: string;
    displayFields?: string[];
    allowAdd?: boolean;
    allowEdit?: boolean;
    allowDelete?: boolean;
    relationFieldSlug?: string;
    [key: string]: unknown;
  };
}

export interface TargetField {
  id: string;
  slug: string;
  name: string;
  fieldType: string;
  isCore: boolean;
  /** Opcjonalne — używane przy fetchach tenant-scope; w embedded zastąpione externalFields. */
  showInList?: boolean;
  sortOrder: number;
}

interface TargetModuleDetail {
  module: {
    slug: string;
    name: string;
    isHelperEntity: boolean;
  };
  fields: TargetField[];
}

interface LayoutSection {
  id: string;
  title: string | null;
  columns: 1 | 2 | 3;
  elements: LayoutElement[];
}

export interface SubcollectionLayoutEditorProps {
  collectionSlug: string;
  /**
   * Custom form layout per parent module (sekcje + pola helper-encji).
   * `undefined` = jeszcze nie sklonowane z helper-encji (effekt seedu uruchomi się
   * przy mountcie gdy layoutQ.data dostępny). Pusty `[]` = świadome ustawienie
   * „brak sekcji" przez admina (seed nie uruchamia się ponownie).
   */
  formLayout?: LayoutSection[];
  onFormLayoutChange: (next: LayoutSection[]) => void;
  /** v2 backward compat — uprawnienia wiersza tabeli (allowAdd/Edit/Delete). */
  element: SubcollectionLayoutElement | undefined;
  onChange: (newElement: SubcollectionLayoutElement) => void;
  /**
   * Tryb embedded (super admin panel) — gdy `true`:
   *  - tenant-scope queries (`targetQ`, `layoutQ`) są wyłączone (enabled: false),
   *  - pola palety pochodzą z `externalFields` (pola helper-encji z definicji kartoteki),
   *  - seed layoutu z layoutQ jest pominięty (brak tenant-scope aktywnego layoutu).
   * Nie wpływa na zapis — `onFormLayoutChange` propaguje zmiany do nadrzędnego layoutConfig.
   */
  embedded?: boolean;
  /**
   * Pola helper-encji podane z zewnątrz (gdy `embedded=true`).
   * Zastępują `targetQ.data.fields` z tenant-scope endpointu.
   * Format identyczny z `TargetField` (id/slug/name/fieldType/isCore/showInList/sortOrder).
   */
  externalFields?: TargetField[];
  /**
   * Domyślny layoutConfig helper-encji podany z zewnątrz (gdy `embedded=true`).
   * Używany przez seed-effect jako fallback gdy `formLayout === undefined` i
   * `layoutQ` jest wyłączony (embedded nie może strzelać tenant-scope query).
   * Format: ten sam co `layoutQ.layoutConfig` — `extractDefaultSectionsFromLayout`
   * wyciąga z niego sekcje.
   */
  externalHelperLayout?: unknown;
  'data-testid'?: string;
}

function generateElementId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `rrt-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}

/**
 * Wyciąga sekcje z aktywnego layoutu helper-encji (do default seedu).
 * Bierze pierwszą zakładkę z section children — to standardowo zakładka „Dane"
 * z layoutu helper-encji (po seed-system-modules).
 */
function extractDefaultSectionsFromLayout(cfg: unknown): LayoutSection[] {
  if (!cfg || typeof cfg !== 'object') return [];
  const c = cfg as { elements?: unknown };
  if (!Array.isArray(c.elements)) return [];

  const tabsElement = (c.elements as Array<{ type?: string; tabs?: unknown }>).find(
    (e) => e?.type === 'tabs',
  );
  if (!tabsElement || !Array.isArray(tabsElement.tabs)) return [];

  const fieldsTab = (tabsElement.tabs as Array<{ children?: unknown }>).find(
    (t) =>
      Array.isArray(t?.children) &&
      (t.children as Array<{ type?: string }>).some((cc) => cc?.type === 'section'),
  );
  if (!fieldsTab || !Array.isArray(fieldsTab.children)) return [];

  return (fieldsTab.children as Array<LayoutElement>)
    .filter((cc) => cc?.type === 'section')
    .map((cc) => {
      const s = (cc.settings ?? {}) as { title?: string | null; columns?: number };
      return {
        id: cc.id ?? generateElementId(),
        title: s.title ?? null,
        columns: (s.columns ?? 2) as 1 | 2 | 3,
        elements: Array.isArray(cc.children) ? cc.children : [],
      };
    });
}

export function SubcollectionLayoutEditor({
  collectionSlug,
  formLayout,
  onFormLayoutChange,
  element,
  onChange,
  embedded = false,
  externalFields,
  externalHelperLayout,
  'data-testid': testId = 'subcollection-layout-editor',
}: SubcollectionLayoutEditorProps) {
  const { t } = useTranslation();

  // Gdy embedded=true — wyłączamy tenant-scope queries, żeby uniknąć 401 → wylogowania
  // w panelu super admina. Pola palety pochodzą z externalFields (z definicji kartoteki).
  const targetQ = useQuery({
    queryKey: ['system-module', collectionSlug],
    queryFn: async () => {
      const res = await api.get<TargetModuleDetail>(`/system-modules/${collectionSlug}`);
      return res.data;
    },
    enabled: !!collectionSlug && !embedded,
  });

  const layoutQ = useSystemModuleActiveLayout(collectionSlug, { enabled: !embedded });

  const targetModule = targetQ.data?.module;
  const targetFields = useMemo(
    () => (embedded ? (externalFields ?? []) : (targetQ.data?.fields ?? [])),
    [embedded, externalFields, targetQ.data?.fields],
  );

  const fieldById = useMemo(() => {
    const m = new Map<string, TargetField>();
    targetFields.forEach((f) => m.set(f.id, f));
    return m;
  }, [targetFields]);

  // Śledzimy kolekcję, dla której wykonano seed — NIE boolean. Edytor jest reużywany
  // między zakładkami podkolekcji (ten sam instance, zmienia się tylko collectionSlug),
  // więc jednorazowy boolean wyciekałby: zaseedowana pierwsza otwarta podkolekcja
  // blokowała seed kolejnych (Adresy/Konta bankowe zostawały puste). Re-seed gdy
  // collectionSlug != ostatnio zaseedowana kolekcja.
  const seededForRef = useRef<string | null>(null);
  useEffect(() => {
    if (seededForRef.current === collectionSlug) return;
    if (formLayout !== undefined) {
      seededForRef.current = collectionSlug;
      return;
    }
    // W trybie embedded layoutQ jest wyłączony — fallback na externalHelperLayout
    // (domyślny layoutConfig helper-encji przekazany z backendu przez definicję kartoteki).
    const sourceConfig = layoutQ.layoutConfig ?? (embedded ? externalHelperLayout : undefined);
    if (!sourceConfig) return;
    const defaultSections = extractDefaultSectionsFromLayout(sourceConfig);
    if (defaultSections.length > 0) {
      seededForRef.current = collectionSlug;
      onFormLayoutChange(defaultSections);
    }
  }, [
    collectionSlug,
    embedded,
    externalHelperLayout,
    formLayout,
    layoutQ.layoutConfig,
    onFormLayoutChange,
  ]);

  const sections = useMemo(() => formLayout ?? [], [formLayout]);

  // v2 permissions (allowAdd/Edit/Delete):
  const currentSettings = element?.settings ?? {};
  const allowAdd = currentSettings.allowAdd ?? true;
  const allowEdit = currentSettings.allowEdit ?? true;
  const allowDelete = currentSettings.allowDelete ?? true;

  function patchPermissions(patch: Partial<SubcollectionLayoutElement['settings']>) {
    const baseElement: SubcollectionLayoutElement = element ?? {
      id: generateElementId(),
      type: 'related_records_table',
      settings: { collection: collectionSlug },
    };
    onChange({
      ...baseElement,
      settings: { ...baseElement.settings, ...patch },
    });
  }

  // ─── Mutacje sekcji + pól ──
  function addSection() {
    const newSection: LayoutSection = {
      id: generateElementId(),
      title: t('tenant.subcollectionLayout.newSectionTitle', { defaultValue: 'Nowa sekcja' }),
      columns: 2,
      elements: [],
    };
    onFormLayoutChange([...sections, newSection]);
  }

  function removeSection(id: string) {
    onFormLayoutChange(sections.filter((s) => s.id !== id));
  }

  function updateSection(id: string, patch: Partial<LayoutSection>) {
    onFormLayoutChange(sections.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  }

  function addFieldToSection(sectionId: string, fieldId: string) {
    const newElement: LayoutElement = {
      id: generateElementId(),
      type: 'field',
      fieldId,
    };
    onFormLayoutChange(
      sections.map((s) =>
        s.id === sectionId ? { ...s, elements: [...s.elements, newElement] } : s,
      ),
    );
  }

  function addLayoutElementToSection(sectionId: string, layoutElementType: string) {
    const newElement: LayoutElement = {
      id: generateElementId(),
      type: layoutElementType,
      settings: {},
    };
    onFormLayoutChange(
      sections.map((s) =>
        s.id === sectionId ? { ...s, elements: [...s.elements, newElement] } : s,
      ),
    );
  }

  function removeFieldFromSection(sectionId: string, elementId: string) {
    onFormLayoutChange(
      sections.map((s) =>
        s.id === sectionId ? { ...s, elements: s.elements.filter((e) => e.id !== elementId) } : s,
      ),
    );
  }

  // ─── DnD callbacks ──
  function handleSectionReorder(activeId: string, overId: string) {
    const fromIdx = sections.findIndex((s) => s.id === activeId);
    const toIdx = sections.findIndex((s) => s.id === overId);
    if (fromIdx === -1 || toIdx === -1) return;
    onFormLayoutChange(arrayMove(sections, fromIdx, toIdx));
  }

  function handleElementReorder(
    sourceContainerId: string,
    activeId: string,
    targetContainerId: string,
    overId: string,
  ) {
    if (sourceContainerId === targetContainerId) {
      // Same-container reorder
      const sectionIdx = sections.findIndex((s) => s.id === sourceContainerId);
      if (sectionIdx === -1) return;
      const section = sections[sectionIdx]!;
      const fromIdx = section.elements.findIndex((e) => e.id === activeId);
      const toIdx = section.elements.findIndex((e) => e.id === overId);
      if (fromIdx === -1 || toIdx === -1) return;
      const reordered = arrayMove(section.elements, fromIdx, toIdx);
      const next = [...sections];
      next[sectionIdx] = { ...section, elements: reordered };
      onFormLayoutChange(next);
      return;
    }
    // Cross-container move
    const sourceSec = sections.find((s) => s.id === sourceContainerId);
    const targetSec = sections.find((s) => s.id === targetContainerId);
    if (!sourceSec || !targetSec) return;
    const movedElement = sourceSec.elements.find((e) => e.id === activeId);
    if (!movedElement) return;
    const newSourceElements = sourceSec.elements.filter((e) => e.id !== activeId);
    // Wstaw przed elementem `overId` w target — albo na końcu jeśli `overId === targetContainerId`.
    let newTargetElements: LayoutElement[];
    if (overId === targetContainerId) {
      newTargetElements = [...targetSec.elements, movedElement];
    } else {
      const overIdx = targetSec.elements.findIndex((e) => e.id === overId);
      if (overIdx === -1) {
        newTargetElements = [...targetSec.elements, movedElement];
      } else {
        newTargetElements = [
          ...targetSec.elements.slice(0, overIdx),
          movedElement,
          ...targetSec.elements.slice(overIdx),
        ];
      }
    }
    onFormLayoutChange(
      sections.map((s) => {
        if (s.id === sourceContainerId) return { ...s, elements: newSourceElements };
        if (s.id === targetContainerId) return { ...s, elements: newTargetElements };
        return s;
      }),
    );
  }

  function handleAddFromPalette(
    kind: 'field' | 'layout-element',
    payload: string,
    sectionId: string,
  ) {
    if (kind === 'field') {
      addFieldToSection(sectionId, payload);
    } else if (kind === 'layout-element') {
      addLayoutElementToSection(sectionId, payload);
    }
  }

  const { sensors, onDragEnd } = usePaletteDnD({
    onAddFromPalette: handleAddFromPalette,
    onSectionReorder: handleSectionReorder,
    onElementReorder: handleElementReorder,
    onAccordionPanelReorder: () => {},
    onStepReorder: () => {},
  });

  // ─── Paleta state (Pola | Klocki) ──
  const [leftPanelTab, setLeftPanelTab] = useState<'fields' | 'elements'>('fields');
  // Wyszukiwarka pól (parytet z paletą kartotek w MenuPalette) — filtr po nazwie/slugu.
  const [fieldQuery, setFieldQuery] = useState('');

  const usedFieldIds = useMemo(() => {
    const ids = new Set<string>();
    for (const s of sections) {
      for (const el of s.elements) {
        if (el.type === 'field' && el.fieldId) ids.add(el.fieldId);
      }
    }
    return ids;
  }, [sections]);

  const availableFields = useMemo(
    () =>
      [...targetFields]
        .filter((f) => !usedFieldIds.has(f.id))
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [targetFields, usedFieldIds],
  );

  // Pola już na canvasie — pokazujemy je w palecie wyszarzone (jak w builderze macierzystym),
  // żeby lewy panel zawsze miał ten sam zestaw pól i nie „skakał" przy zmianie zakładek
  // (zamiast zwijać się do komunikatu „wszystkie pola na canvasie").
  const usedFields = useMemo(
    () =>
      [...targetFields]
        .filter((f) => usedFieldIds.has(f.id))
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [targetFields, usedFieldIds],
  );

  const fieldQ = normalizeSearch(fieldQuery.trim());
  const filteredAvailableFields = fieldQ
    ? availableFields.filter((f) => normalizeSearch(`${f.name} ${f.slug}`).includes(fieldQ))
    : availableFields;
  const filteredUsedFields = fieldQ
    ? usedFields.filter((f) => normalizeSearch(`${f.name} ${f.slug}`).includes(fieldQ))
    : usedFields;

  return (
    <DndContext sensors={sensors} onDragEnd={onDragEnd}>
      <div className="flex min-h-0 flex-1" data-testid={testId}>
        {/* ─── Aside paleta po lewej ──────────────────────────────────────── */}
        <aside
          className="w-64 shrink-0 overflow-y-auto border-r border-border-primary bg-bg-secondary p-3"
          data-testid={`${testId}-palette`}
        >
          <div
            className="mb-3 flex gap-1 border-b border-border-primary"
            role="tablist"
            aria-label={t('tenant.subcollectionLayout.v4.paletteTabsAria', {
              defaultValue: 'Paleta klocków',
            })}
          >
            <button
              type="button"
              role="tab"
              aria-selected={leftPanelTab === 'fields'}
              onClick={() => setLeftPanelTab('fields')}
              className={`flex-1 px-2 py-1.5 text-xs font-medium transition-colors ${
                leftPanelTab === 'fields'
                  ? 'border-b-2 border-accent-500 text-accent-600'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
              data-testid={`${testId}-palette-tab-fields`}
            >
              {t('tenant.subcollectionLayout.v4.paletteTabFields', { defaultValue: 'Pola' })}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={leftPanelTab === 'elements'}
              onClick={() => setLeftPanelTab('elements')}
              className={`flex-1 px-2 py-1.5 text-xs font-medium transition-colors ${
                leftPanelTab === 'elements'
                  ? 'border-b-2 border-accent-500 text-accent-600'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
              data-testid={`${testId}-palette-tab-elements`}
            >
              {t('tenant.subcollectionLayout.v4.paletteTabElements', { defaultValue: 'Elementy' })}
            </button>
          </div>

          {leftPanelTab === 'fields' ? (
            <div className="space-y-1">
              <p className="mb-2 text-[10px] font-medium uppercase tracking-wider text-text-muted">
                {t('tenant.subcollectionLayout.v4.paletteFieldsTitle', {
                  collection: targetModule?.name ?? collectionSlug,
                  defaultValue: `Pola helper-encji: ${targetModule?.name ?? collectionSlug}`,
                })}
              </p>
              {availableFields.length === 0 && usedFields.length === 0 ? (
                <p className="text-xs text-text-tertiary">
                  {t('tenant.subcollectionLayout.v4.paletteFieldsEmpty', {
                    defaultValue: 'Wszystkie pola są już na canvasie.',
                  })}
                </p>
              ) : (
                <>
                  <PaletteSearchInput
                    value={fieldQuery}
                    onChange={setFieldQuery}
                    placeholder={t('common.layoutPalette.searchFields')}
                    testId={`${testId}-palette-fields-search`}
                  />
                  {filteredAvailableFields.map((field) => (
                    <SubcollectionFieldButton
                      key={field.id}
                      field={field}
                      testIdPrefix={`${testId}-palette-field-`}
                    />
                  ))}
                  {/* Pola już na canvasie — wyszarzone, zawsze widoczne (stabilny lewy panel). */}
                  {filteredUsedFields.length > 0 && (
                    <>
                      {filteredAvailableFields.length > 0 && (
                        <hr className="my-2 border-border-primary" />
                      )}
                      {filteredUsedFields.map((field) => (
                        <SubcollectionFieldButton
                          key={field.id}
                          field={field}
                          disabled
                          testIdPrefix={`${testId}-palette-field-`}
                        />
                      ))}
                    </>
                  )}
                  {filteredAvailableFields.length === 0 && filteredUsedFields.length === 0 && (
                    <p className="text-xs text-text-tertiary">
                      {t('common.layoutPalette.noFields')}
                    </p>
                  )}
                </>
              )}
            </div>
          ) : (
            <div data-testid={`${testId}-palette-elements`}>
              <LayoutElementsPalette onAdd={() => {}} />
            </div>
          )}
        </aside>

        {/* ─── Canvas po środku ───────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="flex flex-col gap-6">
            <header
              className="rounded-lg border border-accent-300 bg-accent-50 px-4 py-3 dark:border-accent-700 dark:bg-accent-900/20"
              data-testid={`${testId}-section-header`}
            >
              <div className="flex items-start gap-2">
                <LayoutIcon
                  className="mt-0.5 h-5 w-5 shrink-0 text-accent-600"
                  aria-hidden="true"
                />
                <div className="flex flex-col gap-1">
                  <h3 className="text-sm font-semibold text-accent-900 dark:text-accent-100">
                    {t('tenant.subcollectionLayout.v3.sectionHeader', {
                      defaultValue: 'Layout formularza subkolekcji',
                    })}
                  </h3>
                  <p className="text-xs text-accent-800 dark:text-accent-200">
                    {t('tenant.subcollectionLayout.v3.sectionSubtitle', {
                      defaultValue:
                        'Niezależny formularz dla rekordów modułu wskazywanego w tym tabie. Default = klon layoutu helper-encji. Po wjeździe customizujesz niezależnie.',
                    })}
                  </p>
                </div>
              </div>
            </header>

            <div className="flex flex-col gap-1">
              <h4 className="text-base font-semibold text-text-primary">
                {t('tenant.subcollectionLayout.v3.title', {
                  collection: targetModule?.name ?? collectionSlug,
                  defaultValue: `Layout: ${targetModule?.name ?? collectionSlug}`,
                })}
              </h4>
              <p className="text-sm text-text-tertiary">
                {t('tenant.subcollectionLayout.v3.description', {
                  defaultValue:
                    'Sekcje + pola jak w głównym builderze, ale per parent module. Pola pochodzą z modułu wskazywanego. Przeciągnij z palety po lewej.',
                })}
              </p>
            </div>

            {(targetQ.isLoading || layoutQ.isLoading) && (
              <p className="text-sm text-text-secondary" data-testid={`${testId}-loading`}>
                {t('common.loading')}
              </p>
            )}

            {targetQ.isError && (
              <p
                className="rounded-md border border-error-500/40 bg-error-500/10 px-3 py-2 text-sm text-error-500"
                data-testid={`${testId}-error`}
              >
                {t('tenant.subcollectionLayout.loadError', { collection: collectionSlug })}
              </p>
            )}

            {!targetQ.isLoading && targetFields.length === 0 && !targetQ.isError && (
              <p
                className="rounded-md border border-dashed border-border-secondary bg-bg-secondary px-4 py-6 text-center text-sm text-text-tertiary"
                data-testid={`${testId}-empty-fields`}
              >
                {t('tenant.subcollectionLayout.noFieldsHint', { collection: collectionSlug })}
              </p>
            )}

            {targetFields.length > 0 && (
              <section className="space-y-3" data-testid={`${testId}-sections-canvas`}>
                {sections.length === 0 ? (
                  <div
                    className="rounded-md border border-dashed border-border-secondary bg-bg-secondary px-4 py-8 text-center text-sm text-text-tertiary"
                    data-testid={`${testId}-empty-canvas`}
                  >
                    {t('tenant.subcollectionLayout.v3.emptyCanvas', {
                      defaultValue:
                        'Brak sekcji. Dodaj pierwszą żeby zacząć konfigurować layout, albo poczekaj na default seed z helper-encji.',
                    })}
                  </div>
                ) : (
                  <SortableContext
                    items={sections.map((s) => s.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {sections.map((section) => (
                      <SortableSubcollectionSection
                        key={section.id}
                        section={section}
                        fieldById={fieldById}
                        onUpdateTitle={(title) => updateSection(section.id, { title })}
                        onUpdateColumns={(columns) => updateSection(section.id, { columns })}
                        onRemove={() => removeSection(section.id)}
                        onRemoveField={(elementId) => removeFieldFromSection(section.id, elementId)}
                        testId={`${testId}-section-${section.id}`}
                      />
                    ))}
                  </SortableContext>
                )}

                <button
                  type="button"
                  onClick={addSection}
                  className="inline-flex items-center gap-2 rounded-md border border-border-primary bg-bg-primary px-3 py-1.5 text-sm font-medium text-text-primary hover:bg-bg-secondary"
                  data-testid={`${testId}-add-section-btn`}
                >
                  <Plus className="h-4 w-4" />
                  {t('tenant.subcollectionLayout.v3.addSectionBtn', {
                    defaultValue: '+ Dodaj sekcję',
                  })}
                </button>
              </section>
            )}

            <section className="space-y-2 rounded-md border border-border-secondary bg-bg-secondary p-3">
              <h4 className="text-sm font-semibold text-text-primary">
                {t('tenant.subcollectionLayout.permissionsHeader')}
              </h4>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={allowAdd}
                  onChange={(e) => patchPermissions({ allowAdd: e.target.checked })}
                  data-testid={`${testId}-allow-add`}
                />
                <span>{t('tenant.subcollectionLayout.allowAdd')}</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={allowEdit}
                  onChange={(e) => patchPermissions({ allowEdit: e.target.checked })}
                  data-testid={`${testId}-allow-edit`}
                />
                <span>{t('tenant.subcollectionLayout.allowEdit')}</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={allowDelete}
                  onChange={(e) => patchPermissions({ allowDelete: e.target.checked })}
                  data-testid={`${testId}-allow-delete`}
                />
                <span>{t('tenant.subcollectionLayout.allowDelete')}</span>
              </label>
            </section>

            <div className="flex flex-col gap-1 border-t border-border-secondary pt-3">
              <Link
                to={`/system-modules/${collectionSlug}/settings?tab=layout`}
                className="inline-flex items-center gap-2 text-sm text-accent-600 hover:text-accent-700 hover:underline"
                data-testid={`${testId}-open-target-module`}
              >
                <LayoutIcon className="h-4 w-4" />
                {t('tenant.subcollectionLayout.openTargetModule', {
                  collection: targetModule?.name ?? collectionSlug,
                })}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </DndContext>
  );
}

// ─── SortableSubcollectionSection ──────────────────────────────────────────

interface SortableSubcollectionSectionProps {
  section: LayoutSection;
  fieldById: Map<string, TargetField>;
  onUpdateTitle: (title: string) => void;
  onUpdateColumns: (columns: 1 | 2 | 3) => void;
  onRemove: () => void;
  onRemoveField: (elementId: string) => void;
  testId: string;
}

function SortableSubcollectionSection({
  section,
  fieldById,
  onUpdateTitle,
  onUpdateColumns,
  onRemove,
  onRemoveField,
  testId,
}: SortableSubcollectionSectionProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.id,
    data: { source: 'section', target: 'section', sectionId: section.id },
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  };
  const gridCols =
    section.columns === 1 ? 'grid-cols-1' : section.columns === 3 ? 'grid-cols-3' : 'grid-cols-2';

  const fieldElements = section.elements.filter((el) => el.type === 'field' && el.fieldId);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="rounded-md border border-border-primary bg-bg-primary p-3"
      data-testid={testId}
    >
      <div className="mb-3 flex items-center gap-2">
        <DragHandle
          dragHandleProps={{ ...attributes, ...listeners } as HTMLAttributes<HTMLDivElement>}
          ariaLabel={t('tenant.subcollectionLayout.v4.dragSectionAria', {
            defaultValue: 'Przeciągnij sekcję',
          })}
        />
        <input
          type="text"
          value={section.title ?? ''}
          onChange={(e) => onUpdateTitle(e.target.value)}
          placeholder={t('tenant.subcollectionLayout.v3.sectionTitlePlaceholder', {
            defaultValue: 'Tytuł sekcji…',
          })}
          className="flex-1 border-none bg-transparent px-1 py-0.5 text-sm font-semibold text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-500"
          data-testid={`${testId}-title`}
        />
        <Dropdown
          value={String(section.columns)}
          onChange={(v) => onUpdateColumns(Number(v) as 1 | 2 | 3)}
          className="w-auto rounded px-1 py-0.5 text-xs"
          aria-label={t('tenant.subcollectionLayout.v4.columnsAria', {
            defaultValue: 'Liczba kolumn',
          })}
          data-testid={`${testId}-columns`}
          options={[
            { value: '1', label: '1 kolumna' },
            { value: '2', label: '2 kolumny' },
            { value: '3', label: '3 kolumny' },
          ]}
        />
        <button
          type="button"
          onClick={onRemove}
          className="rounded p-1 text-text-muted hover:bg-bg-secondary hover:text-error-500"
          aria-label={t('common.delete')}
          data-testid={`${testId}-remove-btn`}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <SortableContext
        items={fieldElements.map((el) => el.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className={`grid gap-2 ${gridCols}`} data-testid={`${testId}-fields-grid`}>
          {fieldElements.length === 0 ? (
            <div
              className="col-span-full rounded-md border border-dashed border-border-secondary bg-bg-secondary/50 px-3 py-4 text-center text-xs text-text-tertiary"
              data-testid={`${testId}-empty-fields`}
            >
              {t('tenant.subcollectionLayout.v4.dragFieldHere', {
                defaultValue: 'Przeciągnij pole z palety po lewej',
              })}
            </div>
          ) : (
            fieldElements.map((el) => {
              const field = fieldById.get(el.fieldId!);
              if (!field) {
                return (
                  <div
                    key={el.id}
                    className="rounded border border-error-500/40 bg-error-500/5 px-2 py-1 text-xs text-error-500"
                    data-testid={`${testId}-field-missing-${el.id}`}
                  >
                    {t('tenant.subcollectionLayout.v3.fieldMissing', {
                      defaultValue: 'Pole nie istnieje już w module wskazywanym',
                    })}
                    <button
                      type="button"
                      onClick={() => onRemoveField(el.id)}
                      className="ml-2 underline"
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                );
              }
              return (
                <FieldBlock
                  key={el.id}
                  elementId={el.id}
                  field={{
                    id: field.id,
                    name: field.name,
                    slug: field.slug,
                    fieldType: field.fieldType,
                  }}
                  onRemove={onRemoveField}
                  sortableContainerId={section.id}
                />
              );
            })
          )}
        </div>
      </SortableContext>
    </div>
  );
}
